<template>
	<client-only>
		<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
		<InfoWindow ref="infoWindowContainer" :model-value="isOpen">
			<div ref="infoWindow" class="p-2">
				<h3
					v-if="outlet.content?.outlet_name"
					class="mb-4 max-w-[95%] text-2xl font-medium sm:text-3xl"
				>
					{{ outlet.content.outlet_name }}
				</h3>

				<div class="flex flex-row gap-2 md:gap-6">
					<div
						v-if="
							hoursBody && (hoursBody.tbody.length > 0 || outlet.content?.hours)
						"
						class="max-w-[60%] shrink-0 grow space-y-2"
					>
						<span class="text-sm font-medium text-red-500 sm:text-lg">
							{{ t("opening-hours-title") }}
						</span>

						<div class="my-sm flex flex-col items-start space-y-sm">
							<dl
								v-if="hoursBody"
								class="grid grid-cols-2 gap-x-sm lg:gap-x-lg"
							>
								<template v-for="(tr, index) in hoursBody.tbody" :key="index">
									<dt
										:class="{
											'font-bold': tr.body[0]?.value === currentDayUpperCase,
										}"
									>
										{{ tr.body[0]?.value }}
									</dt>

									<dd
										:class="{
											'font-bold': tr.body[0]?.value === currentDayUpperCase,
										}"
									>
										{{ tr.body[1]?.value }}
									</dd>
								</template>
							</dl>
						</div>

						<nuxt-link
							:to="'/' + outlet.full_slug"
							:aria-label="outlet.content?.outlet_name"
							class="mt-2 inline-block max-w-45 underline md:max-w-none"
						>
							{{ t("exception-hours") }}
						</nuxt-link>
					</div>

					<div class="flex max-w-[60%] grow flex-col space-y-2 sm:block">
						<span class="text-sm font-medium text-red-500 sm:text-lg">
							{{ t("contact-title") }}
						</span>

						<p>{{ outlet.content?.address }}</p>

						<p>{{ outlet.content?.postal }} {{ outlet.content?.city }}</p>

						<a
							:href="`tel:${outlet.content?.phone}`"
							class="mt-sm hover:underline"
						>
							{{ outlet.content?.phone }}
						</a>

						<div
							v-if="
								currentLang === 'fi' &&
								(outlet.content?.trailer_rental ||
									outlet.content?.warehouse_pickup)
							"
							class="grow space-y-2"
						>
							<span class="text-sm font-medium text-red-500 sm:text-lg">
								{{ t("services-title") }}
							</span>

							<div
								class="flex flex-row flex-wrap gap-4 text-xs sm:flex-col sm:flex-nowrap sm:gap-2"
							>
								<div class="flex flex-col">
									<div
										v-if="outlet.content.trailer_rental"
										class="mr-sm flex items-center"
									>
										<v-icon
											icon="trailer"
											class="mr-sm hidden text-2xl text-green-600 md:block"
										/>

										<span>
											{{ t("trailer-rental") }}
										</span>
									</div>

									<div
										v-if="outlet.content.warehouse_pickup"
										class="mr-sm flex items-center"
									>
										<v-icon
											icon="warehouse"
											class="mr-sm hidden text-2xl text-green-600 md:block"
										/>

										<span>
											{{ t("warehouse-ingress") }}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<nuxt-link
					:to="`https://www.google.com/maps/dir/?api=1&origin=My+Location&destination=
                    ${outlet.title},
                    ${outlet.content?.address},
                    ${outlet.content?.postal},
                    ${outlet.content?.city}
                    &travelmode=driving`"
					target="_blank"
					class="mt-4 flex w-fit items-center gap-x-2 underline"
				>
					<v-icon icon="export" class="font-bold" />

					<span>
						{{ t("directions") }}
					</span>
				</nuxt-link>
			</div>
		</InfoWindow>
	</client-only>
</template>

<script setup lang="ts">
import {InfoWindow} from "vue3-google-map";
import {onClickOutside} from "@vueuse/core";
import type {MapMarkerInfo} from "@pkg/shared/types/components/maps";
import type {IHoursTable} from "@pkg/shared/types";

const properties = defineProps<{
	outlet: MapMarkerInfo;
	isOpen: boolean;
}>();

const {t} = useI18n();

const {$i18n} = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);

const infoWindowContainer = ref<InstanceType<typeof InfoWindow> | null>(null);
const infoWindow = shallowRef<HTMLElement | null>(null);

const hoursBody = computed<IHoursTable | undefined>(
	() => properties.outlet.content?.hours_table,
);

const currentDay = new Intl.DateTimeFormat(t("date-locale"), {
	weekday: "long",
}).format();
const currentDayUpperCase =
	currentDay.charAt(0).toUpperCase() + currentDay.slice(1);

onClickOutside(infoWindow, () => {
	infoWindowContainer.value?.close();
});
</script>

<style>
@reference "~/assets/css/tailwind.css";

.gm-style-iw-chr {
	@apply absolute right-0 top-0;
}

.gm-style-iw {
	max-height: 50vh !important;
	max-width: 85vw !important;
	min-width: 280px !important;
}

.gm-style-iw-c {
	@apply p-0!;
}

.gm-style-iw-d {
	max-height: 450px !important;
	@apply p-2 py-4 sm:p-4!;
}
</style>

<i18n lang="json">
{
	"et": {
		"warehouse-hours": "Kättesaamine laost",
		"warehouse-ingress": "Nouda tooted kiireslt meie laost!",
		"trailer-rental": "Pakume ka haagiste renti!",
		"date-locale": "et-ET",
		"contact-title": "Kontaktandmed",
		"opening-hours-title": "Lahtiolekuajad",
		"services-title": "Teenused",
		"directions": "Juhised Google Mapsis",
		"exception-hours": "Vaata erinevaid lahtiolekuaegu poe lehelt"
	},
	"fi": {
		"warehouse-hours": "Nouda tuotteet varastosta",
		"warehouse-ingress": "Nouda tuotteet nopeasti varastoltamme!",
		"trailer-rental": "Meiltä myös peräkärryn vuokraus!",
		"date-locale": "fi-FI",
		"contact-title": "Yhteystiedot",
		"opening-hours-title": "Aukioloajat",
		"services-title": "Palvelut",
		"directions": "Reittiohjeet Google Mapsissa",
		"exception-hours": "Katso poikkeavat aukioloajat myymälän sivulta"
	}
}
</i18n>
